// src/pages/Admin/AdminDashboard.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { productsAPI } from '../../api/products.js';
import { ordersAPI } from '../../api/orders.js';
import { usersAPI } from '../../api/users.js';
import { casesAPI } from '../../api/cases.js';
import { blogAPI } from '../../api/blog.js';

export const AdminDashboard = () => {
  const [stats, setStats] = useState({
    products: 0,
    orders: 0,
    users: 0,
    cases: 0,
    blog: 0,
    revenue: 0,
    pendingOrders: 0,
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setIsLoading(true);
    try {
      const [productsRes, ordersRes, usersRes, casesRes, blogRes] = await Promise.all([
        productsAPI.getAll({ limit: 1 }),
        ordersAPI.getAll({ limit: 10 }),
        usersAPI.getAll({ limit: 1 }),
        casesAPI.getAll({ limit: 1 }),
        blogAPI.getAll({ limit: 1 }),
      ]);

      // Расчет выручки
      const allOrders = ordersRes.data.data || [];
      const revenue = allOrders.reduce((sum, order) => sum + parseFloat(order.total || 0), 0);
      const pendingOrders = allOrders.filter(o => o.status === 'pending' || o.status === 'processing');

      setStats({
        products: productsRes.data.count || 0,
        orders: ordersRes.data.count || 0,
        users: usersRes.data.count || 0,
        cases: casesRes.data.count || 0,
        blog: blogRes.data.count || 0,
        revenue: revenue,
        pendingOrders: pendingOrders.length,
      });
      setRecentOrders(allOrders.slice(0, 5));
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const statCards = [
    { label: 'Товары', value: stats.products, icon: '📦', color: 'rgb(var(--c-accent))', link: '/admin/products' },
    { label: 'Заказы', value: stats.orders, icon: '📋', color: 'rgb(var(--c-success))', link: '/admin/orders' },
    { label: 'Пользователи', value: stats.users, icon: '👥', color: '#6F42C1', link: '/admin/users' },
    { label: 'Кейсы', value: stats.cases, icon: '🏆', color: '#FD7E14', link: '/admin/cases' },
    { label: 'Статьи', value: stats.blog, icon: '✍️', color: '#20C997', link: '/admin/blog' },
    { label: 'Выручка', value: `${stats.revenue.toLocaleString()} ₽`, icon: '💰', color: 'rgb(var(--c-danger))', link: '/admin/orders' },
  ];

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <span>Загрузка статистики...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Статистика */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '32px',
      }}>
        {statCards.map((card, index) => (
          <Link
            key={index}
            to={card.link}
            style={{
              backgroundColor: 'rgb(var(--c-surface))',
              borderRadius: '12px',
              padding: '20px 16px',
              textDecoration: 'none',
              color: 'inherit',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              display: 'block',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '28px' }}>{card.icon}</span>
              <div>
                <div style={{ fontSize: '12px', color: 'rgb(var(--c-text-secondary))', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                  {card.label}
                </div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: card.color }}>
                  {card.value}
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Ожидающие заказы */}
      {stats.pendingOrders > 0 && (
        <div style={{
          backgroundColor: 'rgb(var(--c-warning-bg))',
          border: '1px solid rgb(var(--c-warning))',
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
        }}>
          <span>
            <strong>⚠️ {stats.pendingOrders} заказов</strong> ожидают обработки
          </span>
          <Link
            to="/admin/orders"
            style={{
              backgroundColor: 'rgb(var(--c-text))',
              color: 'rgb(var(--c-on-accent))',
              padding: '6px 16px',
              borderRadius: '100px',
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            Перейти к заказам →
          </Link>
        </div>
      )}

      {/* Последние заказы */}
      {recentOrders.length > 0 && (
        <div style={{
          backgroundColor: 'rgb(var(--c-surface))',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: 600,
            color: 'rgb(var(--c-text))',
            marginBottom: '16px',
          }}>
            Последние заказы
          </h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px',
            }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgb(var(--c-border))' }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left', color: 'rgb(var(--c-text-secondary))', fontWeight: 500 }}>№</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', color: 'rgb(var(--c-text-secondary))', fontWeight: 500 }}>Клиент</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', color: 'rgb(var(--c-text-secondary))', fontWeight: 500 }}>Сумма</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', color: 'rgb(var(--c-text-secondary))', fontWeight: 500 }}>Статус</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', color: 'rgb(var(--c-text-secondary))', fontWeight: 500 }}>Дата</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((order) => (
                  <tr key={order.id} style={{ borderBottom: '1px solid rgb(var(--c-surface-2))' }}>
                    <td style={{ padding: '8px 12px' }}>
                      <Link to={`/admin/orders/${order.id}`} style={{ color: 'rgb(var(--c-accent))', textDecoration: 'none' }}>
                        {order.order_number}
                      </Link>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      {order.User?.first_name || order.guest_email || 'Гость'}
                    </td>
                    <td style={{ padding: '8px 12px', fontWeight: 600 }}>
                      {parseFloat(order.total).toLocaleString()} ₽
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{
                        padding: '2px 10px',
                        borderRadius: '100px',
                        fontSize: '12px',
                        fontWeight: 500,
                        backgroundColor: order.status === 'completed' ? '#D4EDDA' :
                                      order.status === 'cancelled' ? '#F8D7DA' :
                                      order.status === 'processing' ? '#CCE5FF' :
                                      order.status === 'shipped' ? '#CFF3E8' :
                                      '#FFF3CD',
                        color: order.status === 'completed' ? '#155724' :
                               order.status === 'cancelled' ? '#721C24' :
                               order.status === 'processing' ? '#004085' :
                               order.status === 'shipped' ? '#0B5345' :
                               '#856404',
                      }}>
                        {order.status === 'pending' ? 'Ожидает' :
                         order.status === 'processing' ? 'В обработке' :
                         order.status === 'shipped' ? 'Отправлен' :
                         order.status === 'delivered' ? 'Доставлен' :
                         order.status === 'completed' ? 'Завершен' :
                         order.status === 'cancelled' ? 'Отменен' :
                         order.status}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', color: 'rgb(var(--c-text-secondary))' }}>
                      {new Date(order.createdAt).toLocaleDateString('ru-RU')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};